// frontend/src/charts/MatrixView.jsx
// The 'matrix' chart type (Phase 3 step 7e-2, decision D12): Power BI's
// matrix, Looker's pivot table. Row fields nest down the left as a
// collapsible hierarchy (a group's row shows its subtotals and opens to its
// members); column fields spread across the top, each first-level value with
// its own Total and an overall Total at the end; values fill the cells. The
// grand total is the last row. Every number is one the database computed
// (7e-1): nothing is added up here. Headers and the row column stay put
// while the rest scrolls. Numbers follow the chart's presentation (D10);
// the row column and each value's columns take a dragged width
// (presentation.columns[].width, 7d-3) and, with `onResize`, can be dragged.
import React, { useMemo, useState } from 'react';
import { cellText, columnFormatter, columnLabel } from './format';
import { buildMatrix, cellValues, flattenRows, groupKeys } from './matrix';
import { clampWidth, columnTarget, columnWidths, targetKey } from './tableOrder';

const INDENT = 16;        // px per hierarchy level
const CELL_PADDING = 24;  // px-3 on both sides
const SURFACE = 'bg-[color:var(--chart-surface)]';
const LINE = 'border-[color:var(--chart-grid)]';
const SUBTOTAL_BG = { backgroundColor: 'color-mix(in srgb, var(--chart-grid) 45%, var(--chart-surface))' };

function Chevron({ open }) {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" className={`shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}>
      <path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A cell's content box: free until the column has a width, then exactly that wide. */
function Box({ width, title, children, className = '' }) {
  if (width == null) return <div className={className}>{children}</div>;
  return (
    <div title={title} style={{ width: Math.max(0, width - CELL_PADDING) }} className={`overflow-hidden text-ellipsis whitespace-nowrap ${className}`}>
      {children}
    </div>
  );
}

export default function MatrixView({
  columns = [], rows = [], layout, height = 320, presentation = null, onResize = null,
}) {
  const matrix = useMemo(() => buildMatrix(columns, rows, layout), [columns, rows, layout]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const visible = useMemo(() => flattenRows(matrix, collapsed), [matrix, collapsed]);
  const groups = useMemo(() => groupKeys(matrix), [matrix]);
  const widths = columnWidths(presentation && presentation.columns);
  const { rowFields, measures, leafColumns } = matrix;
  // Each value's numbers: the measure's own format under "Automatic" (13c-4), else the chart's.
  const measureText = measures.map((mc) => columnFormatter(presentation, mc));
  const nm = measures.length;
  const anyGroups = groups.length > 0;
  const allCollapsed = anyGroups && groups.every((k) => collapsed.has(k));

  const toggle = (key) => setCollapsed((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const toggleAll = () => setCollapsed(allCollapsed ? new Set() : new Set(groups));

  // Column widths: the row column takes the first row field's; every column of a value takes that value's.
  const rowTarget = rowFields[0] ? columnTarget(rowFields[0]) : null;
  const rowWidth = rowTarget ? widths[targetKey(rowTarget)] : undefined;
  const measureWidth = (mc) => widths[targetKey(columnTarget(mc))];

  // Dragging a heading edge (same pattern as DataTable).
  const [dragging, setDragging] = useState(null);
  const drag = React.useRef(null);
  const liveWidth = (target, stored) => (dragging && dragging.key === targetKey(target) ? dragging.width : stored);
  function startResize(e, target) {
    if (e.button !== 0) return;
    const th = e.currentTarget.parentElement;
    drag.current = { key: targetKey(target), target, startX: e.clientX, startWidth: th.getBoundingClientRect().width };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  }
  function moveResize(e) {
    if (!drag.current) return;
    setDragging({ key: drag.current.key, width: clampWidth(drag.current.startWidth + (e.clientX - drag.current.startX)) });
  }
  function endResize(e) {
    if (!drag.current) return;
    const w = clampWidth(drag.current.startWidth + (e.clientX - drag.current.startX));
    const { target } = drag.current;
    drag.current = null;
    setDragging(null);
    if (onResize) onResize(target, w);
  }
  const handle = (target, label) => onResize && (
    <div
      role="separator" aria-orientation="vertical" aria-label={`Resize ${label}`}
      title="Drag to resize. Double-click for automatic width."
      onPointerDown={(e) => startResize(e, target)} onPointerMove={moveResize} onPointerUp={endResize} onPointerCancel={endResize}
      onDoubleClick={() => onResize(target, null)}
      className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none hover:bg-white/15 active:bg-white/25"
    />
  );

  // Header rows: one per column field, plus the value names when there is more than one value.
  const colLevels = matrix.colFields.length;
  const showMeasureRow = nm > 1 || colLevels === 0;
  const headerRows = colLevels + (showMeasureRow ? 1 : 0);
  const rowHeading = rowFields.map(columnLabel).join(' / ') || 'Rows';
  const rw = liveWidth(rowTarget || {}, rowWidth);
  const rowStyle = rw != null ? { width: rw, minWidth: rw, maxWidth: rw } : undefined;
  const thBase = `whitespace-nowrap border-b ${LINE} px-3 py-2 font-medium text-[color:var(--chart-text-secondary)]`;

  // Group header cells for a level: consecutive leaf columns with the same
  // label at that level share one cell; the overall Total spans every level.
  const spans = (level) => {
    const out = [];
    leafColumns.forEach((lc, i) => {
      if (lc.grand && level > 0) return;
      const label = lc.labels[level];
      const prev = out[out.length - 1];
      const sameGroup = prev && !prev.grand && !lc.grand && prev.label === label
        && (level === 0 ? prev.group === lc.group : prev.parent === lc.labels[0]);
      if (sameGroup) prev.span += 1;
      else out.push({ label, span: 1, group: lc.group, parent: lc.labels[0], total: lc.total, grand: lc.grand, key: `${level}-${i}`, leaf: lc });
    });
    return out;
  };
  // With one value, the last column level is where the value's width and handle live.
  const leafLevel = showMeasureRow ? -1 : colLevels - 1;
  const only = measures[0];
  const onlyWidth = only ? liveWidth(columnTarget(only), measureWidth(only)) : undefined;

  if (!rowFields.length || !leafColumns.length) {
    return <div style={{ height }} className="flex items-center justify-center text-sm text-[color:var(--chart-text-secondary)]">A matrix needs a field on Rows.</div>;
  }

  return (
    <div style={{ maxHeight: height }} className="overflow-auto scrollbar-thin">
      <table className="min-w-full border-collapse text-sm">
        <thead className={`sticky top-0 z-[2] ${SURFACE}`}>
          {Array.from({ length: colLevels }, (_, level) => (
            <tr key={`h${level}`}>
              {level === 0 && (
                <th scope="col" rowSpan={headerRows} style={rowStyle}
                  className={`${thBase} relative sticky left-0 z-[3] ${SURFACE} text-left align-bottom`}>
                  <Box width={rw} title={rowHeading} className="flex items-center gap-1.5">
                    {anyGroups && (
                      <button type="button" onClick={toggleAll} aria-label={allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
                        title={allCollapsed ? 'Expand all' : 'Collapse all'}
                        className="rounded p-0.5 hover:bg-white/10 hover:text-[color:var(--chart-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                        <Chevron open={!allCollapsed} />
                      </button>
                    )}
                    <span className="truncate">{rowHeading}</span>
                  </Box>
                  {rowTarget && handle(rowTarget, rowHeading)}
                </th>
              )}
              {spans(level).map((h) => {
                const leaf = level === leafLevel;
                const w = leaf ? onlyWidth : undefined;
                return (
                  <th key={h.key} scope={leaf ? 'col' : 'colgroup'} colSpan={h.span * nm} rowSpan={h.grand ? colLevels : 1}
                    style={w != null ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    className={`${thBase} relative ${leaf ? 'text-right' : 'text-center'} ${h.total ? 'text-[color:var(--chart-text)]' : ''} ${level < colLevels - 1 || leaf ? `border-l ${LINE}` : ''}`}>
                    <Box width={w} title={h.label} className={leaf ? 'text-right' : ''}>{h.label}</Box>
                    {leaf && handle(columnTarget(only), h.label)}
                  </th>
                );
              })}
            </tr>
          ))}
          {showMeasureRow && (
            <tr>
              {colLevels === 0 && (
                <th scope="col" style={rowStyle} className={`${thBase} relative sticky left-0 z-[3] ${SURFACE} text-left`}>
                  <Box width={rw} title={rowHeading} className="flex items-center gap-1.5">
                    {anyGroups && (
                      <button type="button" onClick={toggleAll} aria-label={allCollapsed ? 'Expand all groups' : 'Collapse all groups'}
                        className="rounded p-0.5 hover:bg-white/10 hover:text-[color:var(--chart-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                        <Chevron open={!allCollapsed} />
                      </button>
                    )}
                    <span className="truncate">{rowHeading}</span>
                  </Box>
                  {rowTarget && handle(rowTarget, rowHeading)}
                </th>
              )}
              {leafColumns.map((lc) => measures.map((mc, mi) => {
                const w = liveWidth(columnTarget(mc), measureWidth(mc));
                return (
                  <th key={`${lc.key}-${mc.alias}`} scope="col" style={w != null ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    className={`${thBase} relative text-right ${lc.total ? 'text-[color:var(--chart-text)]' : ''} ${mi === 0 && colLevels > 0 ? `border-l ${LINE}` : ''}`}>
                    <Box width={w} title={columnLabel(mc)} className="text-right">{columnLabel(mc)}</Box>
                    {handle(columnTarget(mc), columnLabel(mc))}
                  </th>
                );
              }))}
            </tr>
          )}
        </thead>
        <tbody>
          {visible.map(({ node, depth, hasChildren, collapsed: isCollapsed, grand }) => {
            const subtotal = grand || hasChildren || depth < rowFields.length;
            const label = grand ? 'Total' : node.label;
            return (
              <tr key={grand ? '__grand' : node.key} style={subtotal ? SUBTOTAL_BG : undefined}
                className={`border-b ${LINE} last:border-0 ${grand ? 'font-semibold' : subtotal ? 'font-medium' : ''}`}>
                <th scope="row" style={{ ...(rowStyle || {}), ...(subtotal ? SUBTOTAL_BG : {}) }}
                  className={`sticky left-0 z-[1] ${SURFACE} whitespace-nowrap px-3 py-1.5 text-left text-[color:var(--chart-text)] ${
                    grand ? 'font-semibold' : subtotal ? 'font-medium' : 'font-normal'}`}>
                  <Box width={rw} title={label} className="flex items-center gap-1.5" >
                    <span style={{ width: grand ? 0 : (depth - 1) * INDENT }} className="shrink-0" aria-hidden="true" />
                    {hasChildren ? (
                      <button type="button" onClick={() => toggle(node.key)} aria-expanded={!isCollapsed}
                        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${label}`}
                        className="-ml-1 inline-flex items-center gap-1 rounded px-1 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                        <Chevron open={!isCollapsed} /><span className="truncate">{label}</span>
                      </button>
                    ) : (
                      <span className={`truncate ${!grand && depth === rowFields.length && rowFields.length > 1 ? 'pl-[18px]' : ''}`}>{label}</span>
                    )}
                  </Box>
                </th>
                {leafColumns.map((lc) => {
                  const values = cellValues(node, lc, nm);
                  return measures.map((mc, mi) => {
                    const w = liveWidth(columnTarget(mc), measureWidth(mc));
                    // With totals off a group row has no numbers of its own: blank, not "no data".
                    const text = subtotal && !matrix.totals ? '' : cellText(values[mi], measureText[mi]);
                    return (
                      <td key={`${lc.key}-${mc.alias}`}
                        className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-[color:var(--chart-text)] ${mi === 0 && colLevels > 0 ? `border-l ${LINE}` : ''} ${lc.total && !subtotal ? 'font-medium' : ''}`}>
                        <Box width={w} title={w != null ? text : undefined} className="text-right">{text}</Box>
                      </td>
                    );
                  });
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {matrix.headersTruncated && (
        <p className="px-3 py-2 text-xs text-[color:var(--chart-text-muted)]">
          Showing the first {leafColumns.filter((c) => !c.total).length} of {matrix.headerCount.toLocaleString()} column values. Add a filter to narrow it down.
        </p>
      )}
    </div>
  );
}
